import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Menu, X, ArrowUpRight, Instagram, Linkedin, Mail } from "lucide-react";

const NAV = [
  { label: "About", id: "about" },
  { label: "Founders", id: "philosophy" },
  { label: "Partners", id: "partners" },
  { label: "Apply", id: "apply" },
  { label: "Contact", id: "contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  const blur = useTransform(scrollY, [0, 100], [0, 12]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      data-testid="site-header"
      style={{ backdropFilter: `blur(${blur.get()}px)` }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-md border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 h-20 md:h-24 flex items-center justify-between">
        <button
          data-testid="logo-home-link"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="font-serif text-xl md:text-2xl tracking-tight text-white"
        >
          Runway <span className="italic font-light">Foundry</span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => handleAnchor(n.id)}
              className="link-underline text-xs uppercase tracking-[0.22em] text-white/70 hover:text-white transition-colors"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:block">
          <button
            data-testid="nav-apply-cta"
            onClick={() => handleAnchor("apply")}
            className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white border-b border-white pb-1 hover:gap-3 transition-all"
          >
            Apply
            <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.77, 0, 0.18, 1] }}
        style={{ pointerEvents: open ? "auto" : "none", visibility: open ? "visible" : "hidden" }}
        className="md:hidden overflow-hidden bg-black border-t border-white/5"
      >
        <div className="px-6 py-8 flex flex-col gap-6">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`mobile-nav-${n.id}`}
              onClick={() => handleAnchor(n.id)}
              className="text-left font-serif text-3xl text-white"
            >
              {n.label}
            </button>
          ))}
          <div className="flex items-center gap-5 pt-4 text-white/60">
            <a href="https://instagram.com/runwayfoundry" target="_blank" rel="noreferrer" aria-label="Instagram"><Instagram size={18} /></a>
            <a href="https://linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn"><Linkedin size={18} /></a>
            <a href="mailto:hello@runwayfoundry.com" aria-label="Email"><Mail size={18} /></a>
          </div>
        </div>
      </motion.div>
    </motion.header>
  );
}
